import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Activity, CheckCircle2, XCircle, MinusCircle, RefreshCw, Loader2 } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { Button } from '../../../../components/ui/Button';

interface IntegrationStatus {
  type: string;
  name: string;
  status: 'active' | 'error' | 'disabled';
  lastActivity?: string;
  errorCount?: number;
}

const STATUS_CONFIG = {
  active:   { icon: CheckCircle2,  color: 'text-green-500',  bg: 'bg-green-500/10',  label: 'Actif' },
  error:    { icon: XCircle,       color: 'text-destructive', bg: 'bg-destructive/10', label: 'Erreur' },
  disabled: { icon: MinusCircle,   color: 'text-muted-foreground', bg: 'bg-muted/50',  label: 'Désactivé' },
};

export function IntegrationStatusWidget() {
  const [statuses, setStatuses] = useState<IntegrationStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const res = await settingsApi.getIntegrationStatus();
      if (res.success && res.data?.integrations) {
        setStatuses(res.data.integrations);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activeCount = statuses.filter(s => s.status === 'active').length;
  const errorCount  = statuses.filter(s => s.status === 'error').length;

  if (isLoading) {
    return (
      <Card title="Statut des intégrations" icon={Activity}>
        <div className="flex items-center justify-center h-24 gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Vérification des connexions...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Statut des intégrations" icon={Activity}>
      <div className="space-y-4">
        {/* Summary row */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-foreground font-medium">{activeCount}</span>
            <span className="text-muted-foreground">actives</span>
          </div>
          {errorCount > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <span className="h-2 w-2 rounded-full bg-destructive" />
              <span className="text-foreground font-medium">{errorCount}</span>
              <span className="text-muted-foreground">en erreur</span>
            </div>
          )}
          <div className="ml-auto">
            <Button
              variant="ghost"
              size="sm"
              icon={isRefreshing ? Loader2 : RefreshCw}
              onClick={() => load(true)}
              disabled={isRefreshing}
              className={isRefreshing ? 'animate-spin' : ''}
            >
              Actualiser
            </Button>
          </div>
        </div>

        {/* Integration list */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {statuses.map(item => {
            const cfg = STATUS_CONFIG[item.status] ?? STATUS_CONFIG.disabled;
            const Icon = cfg.icon;
            return (
              <div key={item.type} className={`flex items-start gap-3 p-3 rounded-lg ${cfg.bg}`}>
                <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${cfg.color}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                  <p className={`text-xs ${cfg.color}`}>{cfg.label}</p>
                  {item.lastActivity && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(item.lastActivity).toLocaleString()}
                    </p>
                  )}
                  {item.errorCount != null && item.errorCount > 0 && (
                    <p className="text-xs text-destructive">{item.errorCount} erreur(s)</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
